import React from 'react';
import { useForm } from 'react-hook-form';
import { Location } from '../../types';
import Input from '../Form/Input';
import Button from '../Form/Button';

interface LocationFormProps {
  location?: Location;
  onSubmit: (data: Partial<Location>) => void;
  onCancel: () => void;
}

interface LocationFormData {
  address: string;
  isActive: boolean;
}

const LocationForm: React.FC<LocationFormProps> = ({ location, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LocationFormData>({
    defaultValues: location ? {
      address: location.address,
      isActive: location.isActive
    } : {
      address: '',
      isActive: true
    }
  });

  const handleFormSubmit = (data: LocationFormData) => {
    onSubmit(data);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {location ? 'Edit Location' : 'Add New Location'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {location ? 'Update the location address below.' : 'Add a new location address for coast guard permits.'}
          </p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Location Information */}
          <div className="border-b border-gray-200 pb-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Location Information</h4>
            
            <div className="space-y-4">
              <Input
                label="Address *"
                {...register('address', { required: 'Address is required' })}
                error={errors.address?.message}
                placeholder="e.g., Port Said, Egypt"
              />

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active (Available for use in permits)
                </label>
              </div>
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Location Usage
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>This location address will be used as a dropdown option in coast guard permits for both personnel and cars.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : location ? 'Update Location' : 'Add Location'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;
